import { ComponentCategory, EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import FolderClient from '../client/folder/FolderClient';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import { notBlank, notUndefined } from '../util/Assert';
import ComponentName from '../constant/ComponentName';
import React from 'react';
import ComponentCategoryUtil from '../util/ComponenyCategoryUtil';
import { dispatch, store } from '../redux/store';
import { changeActivePageIndex, changeCurrentCategory, changeEnrichedCategories, changePagePath } from '../redux/actions/PageActions';
import { initialPagePath, PagePath, PageState } from '../redux/reducers/PageReducer';
import Icon from '../icons/Icon';
import FolderUtil from '../util/FolderUtil';
import { changeCategory } from '../redux/actions/CategoryActions';
import PageUtil from '../util/PageUtil';
import { getComponentIcon } from '../util/GetComponentIcon';
import LanguageUtil from '../util/LanguageUtil';

class FolderService {
  private static folderClient: FolderClient;

  constructor() {
    FolderService.folderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);
  }

  public async fixComponent(component: ComponentName) {
    notUndefined(component, 'Component cannot be undefined');
    try {
      await FolderService.folderClient.fix(component);
    } catch (e) {}
  }

  public static async calculate(
    type: 'UPDATE' | 'CREATE' | 'WITHOUT_FOLDER' | 'CONTAINER' | 'SWITCH_TO_CATEGORY',
    component: ComponentName,
    url: string,
    pageState: PageState,
    id?: string,
    categories?: ComponentCategory[],
  ) {
    notUndefined(type, 'Type cannot be undefined');
    notUndefined(component, 'Component cannot be undefined');
    notBlank(type, 'Type cannot be blank');
    notBlank(component, 'Component cannot be blank');
    if (type === 'UPDATE' || type == 'SWITCH_TO_CATEGORY') {
      notUndefined(id, 'Id cannot be undefined');
      notBlank(id, 'Id cannot be blank');
    }
    if (type == 'SWITCH_TO_CATEGORY') {
      notUndefined(categories, 'Categories cannot be undefined');
    }
    notUndefined(url, 'Url cannot be undefined');
    notBlank(url, 'Url cannot be blank');
    switch (type) {
      case 'UPDATE':
        await this.calculateUpdate(component, url, id!);
        break;
      case 'CREATE':
        await this.calculateCreate(component, url, pageState);
        break;
      case 'WITHOUT_FOLDER':
        await this.calculateWithoutFolder(component, url, pageState, id);
        break;
      case 'CONTAINER':
        await this.calculateContainer(component, url, pageState);
        break;
      case 'SWITCH_TO_CATEGORY':
        await this.calculateSwitchToCategory(component, url, pageState, id!, categories!);
    }
  }

  private static async calculateUpdate(component: ComponentName, url: string, id: string) {
    const { categoryMap, enrichedMainCategory } = await this.getEnrichedComponentCategories(component);
    if (enrichedMainCategory == undefined) return;
    dispatch(changeEnrichedCategories(categoryMap));
    const componentCategory = Object.values(categoryMap).find((value) => value.components.includes(id));
    if (componentCategory == undefined) return;
    const { pages } = this.switchToCategory(component, componentCategory, categoryMap);
    const currentPath: PagePath = {
      title: id,
      path: url,
      type: 'componentId',
      payload: componentCategory,
      icon: <Icon icon={getComponentIcon(component)} size={16} />,
    };
    pages.push(currentPath);
    dispatch(changePagePath(pages));
    dispatch(changeActivePageIndex(pages.length - 1));
  }

  private static async calculateCreate(component: ComponentName, url: string, pageState: PageState) {
    let { currentCategory, lastCategory } = pageState;
    const { categoryMap, enrichedMainCategory } = await this.getEnrichedComponentCategories(component);
    if (enrichedMainCategory == undefined) return;
    dispatch(changeEnrichedCategories(categoryMap));
    if (currentCategory == undefined && lastCategory != undefined) currentCategory = lastCategory;
    let pages;
    if (currentCategory == undefined) {
      pages = this.switchToCategory(component, enrichedMainCategory, categoryMap).pages;
    } else {
      pages = this.switchToCategory(component, currentCategory, categoryMap).pages;
    }
    const currentPath: PagePath = {
      title: 'Create',
      path: url,
      type: 'componentId',
      payload: currentCategory ?? enrichedMainCategory,
      icon: <Icon icon={getComponentIcon(component)} size={16} />,
    };
    pages.push(currentPath);
    dispatch(changePagePath(pages));
    dispatch(changeActivePageIndex(pages.length - 1));
  }

  private static async calculateWithoutFolder(component: ComponentName, url: string, pageState: PageState, id?: string) {
    const enrichedMainCategory: EnrichedComponentCategory = {
      id: 'Main',
      name: 'Main',
      path: ['Main'],
      components: id ? [id] : [],
      categories: [],
      collaborators: [],
    };
    const categoryMap = {
      ['Main']: enrichedMainCategory,
    };
    dispatch(changeEnrichedCategories(categoryMap));
    let pages = this.switchToCategory(component, enrichedMainCategory, categoryMap).pages;
    dispatch(changePagePath(pages));
    dispatch(changeActivePageIndex(pages.length - 1));
  }

  private static async calculateContainer(component: ComponentName, url: string, pageState: PageState) {
    const { categoryMap } = await this.getEnrichedComponentCategories(component);
    const main = categoryMap['Main'];
    const { pages } = this.switchToCategory(component, main, categoryMap);
    dispatch(changePagePath(pages));
    dispatch(changeActivePageIndex(0));
    dispatch(changeEnrichedCategories(categoryMap));
    dispatch(changeCurrentCategory(main));
  }

  private static async getEnrichedComponentCategories(component: ComponentName) {
    const categories: ComponentCategory[] = await this.folderClient.findMany(component);
    const categoryMap: Record<string, EnrichedComponentCategory> = {};
    const enrichedMainCategory = ComponentCategoryUtil.enrichMainCategory('Main', categories, [], categoryMap);
    return {
      categoryMap,
      enrichedMainCategory,
    };
  }

  public static async calculateCase(url: string, domain?: string, id?: string) {
    const initial = initialPagePath[ComponentName.CASE];
    const pagePaths: PagePath[] = [
      {
        title: initial.title,
        path: initial.path,
        icon: <Icon icon={getComponentIcon(ComponentName.CASE)} className={'text-f-black-2'} size={16} />,
        type: 'category',
        categoryId: 'Main',
        categoryPath: 'Main',
        payload: PageUtil.calculateDefaultCategory(),
      },
    ];
    const previousPagePath = store.getState().page.pagePath;

    let calculatedDomain = domain;
    const previousDomain: PagePath | undefined = previousPagePath.find((page: PagePath) => page.type == 'category' && page.payload?.id == 'Domain');

    const previousId: PagePath | undefined = previousPagePath.find((page: PagePath) => page.type == 'componentId');
    let calculatedId = id;
    if (previousId) {
      calculatedId = previousId.title;
    }
    if (previousDomain) {
      calculatedDomain = previousDomain.title;
    }

    if (calculatedDomain) {
      const url = domain ? `cases/${domain}` : previousDomain?.path || initial.path;
      pagePaths.push({
        title: domain ? domain : calculatedDomain,
        path: url,
        type: 'category',
        categoryId: 'Domain',
        categoryPath: 'Main.Domain',
        payload: {
          id: 'Domain',
          name: 'Domain',
          path: ['Main', 'Domain'],
          components: [],
          categories: [],
          collaborators: [],
        },
      });
    }
    if (calculatedId) {
      const _url = id != undefined ? url : previousId?.path;
      pagePaths.push({
        title: calculatedId,
        path: _url,
        icon: <Icon icon={getComponentIcon(ComponentName.CASE)} className={'text-f-black-2'} size={16} />,
        type: 'componentId',
        categoryId: 'Id',
        categoryPath: 'Main.Domain.Id',
        domain: domain,
        payload: {
          id: 'Id',
          name: 'Id',
          path: ['Main', 'Domain', 'Id'],
          components: [],
          categories: [],
          collaborators: [],
        } as EnrichedComponentCategory,
      });
    }
    const categoryMap: Record<string, any> = {
      ['Main']: pagePaths[0].payload,
    };
    if (calculatedDomain != undefined) {
      categoryMap['Main.Domain'] = pagePaths[1].payload;
    }
    if (calculatedId) {
      categoryMap['Main.Domain.Id'] = pagePaths[2].payload;
    }
    dispatch(changePagePath(pagePaths));
    dispatch(changeEnrichedCategories(categoryMap));
    if (id) {
      dispatch(changeCurrentCategory(pagePaths[2].payload));
      dispatch(changeActivePageIndex(2));
    } else if (domain) {
      dispatch(changeCurrentCategory(pagePaths[1].payload));
      dispatch(changeActivePageIndex(1));
    } else {
      dispatch(changeCurrentCategory(pagePaths[0].payload));
      dispatch(changeActivePageIndex(0));
    }
  }

  public static async calculateAch(fileName?: string, transactionId?: string, url?: string) {
    const initial = initialPagePath[ComponentName.ACH];
    const pagePaths: PagePath[] = [
      {
        title: initial?.title ?? LanguageUtil.get({ id: 'pages.ach.title', defaultMessage: 'Automated Clearing House' }),
        path: '/ach',
        icon: <Icon icon={getComponentIcon(ComponentName.ACH)} className={'text-f-black-2'} size={16} />,
        type: 'category',
        categoryId: 'Main',
        categoryPath: 'Main',
        payload: PageUtil.calculateDefaultCategory(),
      },
    ];

    const previousPagePath = store.getState().page.pagePath;
    const previousFileName: PagePath | undefined = previousPagePath.find((page: PagePath) => page.type == 'category' && page.payload?.id == 'File');
    const previousTransactionId: PagePath | undefined = previousPagePath.find((page: PagePath) => page.type == 'componentId');

    let calculatedFileName = fileName;
    let calculatedTransactionId = transactionId;

    if (previousFileName && !fileName) {
      calculatedFileName = previousFileName.title.replace('.ach', '');
    }
    if (previousTransactionId && !transactionId) {
      calculatedTransactionId = previousTransactionId.title;
    }

    if (calculatedFileName) {
      const fileDetailPath = fileName ? `/ach/${encodeURIComponent(calculatedFileName)}` : previousFileName?.path || '/ach';
      pagePaths.push({
        title: calculatedFileName,
        path: fileDetailPath,
        icon: <Icon icon={'folder'} className={'text-f-black-2'} size={16} />,
        type: 'category',
        categoryId: 'File',
        categoryPath: 'Main.File',
        payload: {
          id: 'File',
          name: 'File',
          path: ['Main', 'File'],
          components: [],
          categories: [],
          collaborators: [],
        },
      });
    }

    if (calculatedTransactionId) {
      const transactionDetailPath = url || previousTransactionId?.path || '/ach';
      const shortTransactionId = calculatedTransactionId.length > 24 ? calculatedTransactionId.slice(0, 24) + '...' : calculatedTransactionId;
      pagePaths.push({
        title: shortTransactionId,
        path: transactionDetailPath,
        icon: <Icon icon={getComponentIcon(ComponentName.ACH)} className={'text-f-black-2'} size={16} />,
        type: 'componentId',
        categoryId: 'Transaction',
        categoryPath: 'Main.File.Transaction',
        payload: {
          id: 'Transaction',
          name: 'Transaction',
          path: ['Main', 'File', 'Transaction'],
          components: [],
          categories: [],
          collaborators: [],
        } as EnrichedComponentCategory,
      });
    }

    const categoryMap: Record<string, EnrichedComponentCategory> = {
      ['Main']: pagePaths[0].payload,
    };
    if (calculatedFileName) {
      categoryMap['Main.File'] = pagePaths[1].payload;
    }
    if (calculatedTransactionId) {
      categoryMap['Main.File.Transaction'] = pagePaths[2].payload;
    }

    dispatch(changePagePath(pagePaths));
    dispatch(changeEnrichedCategories(categoryMap));

    if (transactionId) {
      dispatch(changeCurrentCategory(pagePaths[2].payload));
      dispatch(changeActivePageIndex(2));
    } else if (fileName) {
      dispatch(changeCurrentCategory(pagePaths[1].payload));
      dispatch(changeActivePageIndex(1));
    } else {
      dispatch(changeCurrentCategory(pagePaths[0].payload));
      dispatch(changeActivePageIndex(0));
    }
  }

  private static switchToCategory = (
    component: ComponentName,
    enrichCategory: EnrichedComponentCategory,
    enrichedCategories: Record<string, EnrichedComponentCategory>,
  ) => {
    let payload: EnrichedComponentCategory;
    if (enrichCategory && enrichCategory.id == 'Main') {
      payload = enrichCategory;
    } else {
      payload = enrichedCategories['Main'];
    }
    const initialPath = initialPagePath[component];
    const pages: PagePath[] = [
      {
        title: initialPath?.title,
        categoryPath: 'Main',
        icon: <Icon size={16} icon={getComponentIcon(component)} />,
        type: 'category',
        categoryId: 'Main',
        path: initialPath.path,
        payload: payload,
      },
    ];
    FolderUtil.calculateCurrentCategoryPath(enrichCategory, pages, enrichedCategories, component.toLowerCase());
    dispatch(changePagePath(pages));
    dispatch(changeActivePageIndex(enrichCategory.path.length - 1));
    dispatch(changeCategory({ component: component, activePage: enrichCategory.id }));
    dispatch(changeCurrentCategory(enrichCategory));
    return {
      pages,
    };
  };

  private static async calculateSwitchToCategory(component: ComponentName, url: string, pageState: PageState, id: string, categories: ComponentCategory[]) {
    const { currentCategory, activePageIndex, pagePath } = pageState;
    const categoryMap: Record<string, EnrichedComponentCategory> = {};
    ComponentCategoryUtil.enrichMainCategory('Main', categories, [], categoryMap);
    const current = Object.values(categoryMap).find((value) => value.id == id);
    dispatch(changeEnrichedCategories(categoryMap));
    if (current) {
      if (currentCategory == undefined) {
        this._switch(current, pageState, component, categoryMap);
      } else {
        const activePath: PagePath = pagePath[activePageIndex];
        if (activePath && activePath.type == 'category') {
          dispatch(changeCurrentCategory(categoryMap[activePath.categoryPath]));
        } else if (activePageIndex == 0) {
          this._switch(current, pageState, component, categoryMap);
        }
      }
    }
  }

  private static _switch(
    enrichCategory: EnrichedComponentCategory,
    pageState: PageState,
    component: ComponentName,
    enrichedCategories: Record<string, EnrichedComponentCategory>,
  ) {
    let payload: EnrichedComponentCategory;
    if (enrichCategory && enrichCategory.id == 'Main') {
      payload = enrichCategory;
    } else {
      payload = enrichedCategories['Main'];
    }
    const pageInfo = initialPagePath[component];
    const pages: PagePath[] = [
      {
        title: pageInfo?.title ?? component,
        categoryPath: 'Main',
        path: `/${component.toLowerCase()}`,
        icon: <Icon size={16} icon={getComponentIcon(component)} />,
        type: 'category',
        payload: payload,
      },
    ];

    const p = FolderUtil.calculateCurrentCategoryPath(enrichCategory, pages, enrichedCategories, component.toLowerCase());
    dispatch(changePagePath(pages));
    dispatch(changeActivePageIndex(enrichCategory.path.length - 1));
    dispatch(changeCategory({ component: component, activePage: enrichCategory.id }));
    dispatch(changeCurrentCategory(enrichCategory));
  }
}

export default FolderService;
